import {json, redirect} from '@remix-run/node'
import {
  Form,
  Link,
  useActionData,
  useLoaderData,
  useRouteLoaderData,
  useSearchParams,
} from '@remix-run/react'
import {withZod} from '@remix-validated-form/with-zod'
import {z} from 'zod'
import {zfd} from 'zod-form-data'
import {Alert} from '~/components/Alert'
import {Button, LinkButton} from '~/components/Button'
import {TextInput} from '~/components/Form/TextInput'
import {RestartAuthFlow} from '~/components/RestartAuthFlow'
import {magicLinkSessionErrorKey} from '~/constants/authentication'
import {authenticator} from '~/services/auth.server'
import {
  commitSession,
  destroyOidcSession,
  getUserSession,
} from '~/services/session.server'
import {OIDC_PRESERVE_PARAMS, clientEnvVariablesSchema} from '~/types'
import type {
  ActionArgs,
  LoaderArgs,
  SerializeFrom,
  Session,
} from '@remix-run/node'
import type {loader as rootLoader} from '../root'

function preserveOidcParams(searchParams: URLSearchParams) {
  const preserved = new URLSearchParams()
  OIDC_PRESERVE_PARAMS.forEach((param) => {
    const value = searchParams.get(param)
    if (value) preserved.set(param, value)
  })
  return preserved
}

async function handleLoginTypeRedirect({
  loginTypeParam,
  loginTypeEnv,
  searchParams,
  session,
}: {
  loginTypeParam: string
  loginTypeEnv: string
  searchParams: URLSearchParams
  session: Session
}) {
  if (!['keycloak', 'oidc'].includes(loginTypeParam)) return null
  if (!['KEYCLOAK', 'OIDC'].includes(loginTypeEnv)) return null

  session.set('loginType', loginTypeParam)

  if (loginTypeParam === 'keycloak') {
    return redirect('/keycloak', {
      headers: {'Set-Cookie': await commitSession(session)},
    })
  }

  if (loginTypeParam === 'oidc') {
    const preserved = preserveOidcParams(searchParams)
    preserved.set('provider', 'oidc')
    const oidcRedirect = preserved.toString()
      ? `/auth/dowjones?${preserved}`
      : '/auth/dowjones'

    return redirect(oidcRedirect, {
      headers: {'Set-Cookie': await commitSession(session)},
    })
  }

  return null
}

const REQUEST_A_TRIAL_URL_BASE =
  'https://dragonflyintelligence.com/get-in-touch/'

export default function LoginLink() {
  const {ENV} = useRouteLoaderData('root') as SerializeFrom<typeof rootLoader>
  const actionData = useActionData<typeof action>()
  const errors = actionData?.error?.fieldErrors
  const [searchParams] = useSearchParams()
  const redirect = searchParams.get('redirectTo')
  const utmSource = searchParams.get('utm_source')
  const requestATrialUrl = `${REQUEST_A_TRIAL_URL_BASE}?utm_source=${utmSource}&utm_medium=referral&utm_campaign=client&utm_content=gated-article`
  const {magicLinkSent, error} = useLoaderData<typeof loader>()

  if (magicLinkSent) {
    return <RestartAuthFlow />
  }

  const actionMap = {
    KEYCLOAK: '/auth/keycloak',
    OIDC: '/auth/dowjones',
  }

  return (
    <div>
      {error && (
        <Alert type="error" className="mb-4">
          {error.message}
        </Alert>
      )}

      {ENV.LOGIN_TYPE === 'DRAGONFLY' ? (
        <div className="rounded-md bg-white/90 p-4 shadow-2xl backdrop-blur-sm">
          <Form method="POST" autoComplete="on">
            <div className="grid grid-cols-1 gap-6">
              <TextInput
                type="text"
                name="email"
                label="Email"
                error={errors?.email}
              />
              {redirect && (
                <input type="hidden" name="redirectTo" value={redirect} />
              )}
              <Button>Send me a login link</Button>
            </div>
          </Form>

          <div className="relative mt-6 px-2 text-center text-sm font-medium text-gray-500">
            Or login with a{' '}
            <Link to="/password-login" className="underline">
              username and password
            </Link>
          </div>
        </div>
      ) : (
        <div className="max-w-96 flex w-full flex-col items-center justify-center gap-5">
          <Form
            method="POST"
            action={actionMap[ENV.LOGIN_TYPE]}
            className="w-full"
          >
            {/* Preserve specific URL parameters through OIDC authentication */}
            {OIDC_PRESERVE_PARAMS.map((paramName) => {
              const paramValue = searchParams.get(paramName)
              return paramValue ? (
                <input
                  key={paramName}
                  type="hidden"
                  name={paramName}
                  value={paramValue}
                />
              ) : null
            })}
            <Button name="action" className="w-full">
              Sign in
            </Button>
          </Form>
          <LinkButton
            to={requestATrialUrl}
            intent="secondary"
            className="w-full"
          >
            Request a trial
          </LinkButton>
        </div>
      )}
    </div>
  )
}
